import type { BoundingBox } from '@aus-platform/cesium';
import { Cartographic, Rectangle } from 'cesium';
import { compact, filter, isEmpty, isNil } from 'lodash';
import { type DrawingTools } from 'map-3d/shared/map-3d-tools';
import type { SiteListItem } from 'shared/api';
import { FileType, LayerQueryKey, LayerType, queryClient } from 'shared/api';
import { type CesiumProxy } from 'shared/cesium';
import { isCoordinateWithinBounds } from 'shared/helpers';
import { type SwipeMapLayer } from 'src/swipe-map-3d';
import { getPointCloudMetadata, removeAllFeatures, removeMapLayer } from '.';
import {
  type WorkspaceLayer,
  type WorkspaceLayerListObj,
} from '../../../../shared';

export const getLayerMetadata = (layer: WorkspaceLayer | SwipeMapLayer) => {
  let properties;

  switch (layer.type) {
    case LayerType.Orthomosaic:
      properties = layer.files?.find(
        (file) => file?.type === FileType.OrthomosaicCog,
      )?.properties;
      break;
    case LayerType.SlopeMap:
      properties = layer.files?.find(
        (file) => file?.type === FileType.SlopeMap,
      )?.properties;
      break;
    case LayerType.MBTiles:
    case LayerType.Contour:
      properties = layer.tiles?.properties;
      break;
    case LayerType.CapturedDsm:
      properties = layer.histogramData?.metadata;
      break;
    case LayerType.PointCloud:
      properties = getPointCloudMetadata(layer);
      break;
    default:
      properties = layer.properties;
      break;
  }

  return properties;
};

/**
 * Initially zIndex is undefined when layer response is loading,
 * thus using 9999 in it's place.
 *
 * @param layers WorkspaceLayer[]
 */
export const sortLayers = (layers: WorkspaceLayer[]) => {
  return layers.sort((layer1, layer2) => {
    const zIndex1 = layer1.zIndex ?? Number.MAX_VALUE;
    const zIndex2 = layer2.zIndex ?? Number.MAX_VALUE;

    return zIndex2 - zIndex1;
  });
};

export const refetchSingleLayer = (layerId: string) => {
  queryClient.refetchQueries({
    queryKey: [`${LayerQueryKey.Layers}/${layerId}`],
    exact: true,
  });
};

/**
 * Sets the visibility of vector layers.
 * @param workspaceLayers WorkspaceLayerListObj
 * @param show boolean
 */
export const setVectorLayerVisibility = (
  workspaceLayers: WorkspaceLayerListObj,
  show: boolean,
) => {
  const vectorLayers = filter(
    workspaceLayers,
    (layer) => layer.type === LayerType.Vector,
  );

  vectorLayers.forEach((activeLayer) => {
    if (activeLayer.mapLayer) {
      activeLayer.mapLayer.show = show;
    }
  });
};

/**
 * Sets visibility for every layer whose type is NOT in visibleTypes,
 * returning a snapshot of each affected layer's previous `mapLayer.show`
 * value so it can be restored later.
 *
 * @param workspaceLayers WorkspaceLayerListObj
 * @param visibleTypes LayerType[] — types to leave untouched
 * @param show boolean — visibility to apply to the remaining layers
 * @returns Record<string, boolean> — layerId -> previous `mapLayer.show` value
 */
export const toggleLayersVisibilityExcludingTypes = (
  workspaceLayers: WorkspaceLayerListObj,
  visibleTypes: LayerType[],
  show: boolean,
): Record<string, boolean> => {
  const layersToToggle = filter(
    workspaceLayers,
    (layer) => !visibleTypes.includes(layer.type),
  );

  const previousVisibility: Record<string, boolean> = {};

  layersToToggle.forEach((activeLayer) => {
    if (activeLayer.mapLayer) {
      previousVisibility[activeLayer.id] = activeLayer.mapLayer.show;
      activeLayer.mapLayer.show = show;
    }
  });

  return previousVisibility;
};

/**
 * Restores each layer's `mapLayer.show` to the value captured in visibilitySnapshot.
 *
 * @param workspaceLayers WorkspaceLayerListObj
 * @param visibilitySnapshot Record<string, boolean> — layerId -> visibility to restore
 */
export const restoreLayersVisibility = (
  workspaceLayers: WorkspaceLayerListObj,
  visibilitySnapshot: Record<string, boolean>,
) => {
  Object.entries(visibilitySnapshot).forEach(([layerId, show]) => {
    const mapLayer = workspaceLayers[layerId]?.mapLayer;

    if (mapLayer) {
      mapLayer.show = show;
    }
  });
};

/**
 * Remove layers features and map layer from the map
 *
 * @param layer WorkspaceLayer
 * @param drawingTools DrawingTools | null
 * @param cesiumProxy CesiumProxy | null
 */
export const removeWorkspaceLayersFromMap = (
  layers: WorkspaceLayer[],
  drawingTools: DrawingTools | null,
  cesiumProxy: CesiumProxy | null,
) => {
  layers.forEach((layer) => {
    if (layer.features && drawingTools) {
      removeAllFeatures(layer, drawingTools);
    }

    if (layer.mapLayer && cesiumProxy) {
      removeMapLayer(cesiumProxy, layer.mapLayer);
    }
  });
};

/**
 * Checks whether the cursor is within the layer's COG bounds.
 *
 * @param layer - The workspace layer whose COG bounds are checked.
 * @param cursor - The cursor's longitude and latitude coordinates.
 * @returns `true` if the cursor is within the layer's COG bounds; otherwise, `false`.
 */
export const isCursorWithinLayerBounds = (
  layer: WorkspaceLayer,
  cursor?: { longitude: number; latitude: number } | null,
) => {
  const bounds = getLayerMetadata(layer)?.bounds;

  if (isNil(cursor) || isNil(bounds)) {
    return false;
  }

  return isCoordinateWithinBounds(cursor.longitude, cursor.latitude, bounds);
};

/**
 * Zooms to a layer in case it's bounds are present else zooms to the current site's bounds.
 * @param layer WorkspaceLayer
 * @param cesiumProxy CesiumProxy
 * @param site SiteListItem | null
 */
export const zoomToLayer = (
  layer: WorkspaceLayer,
  cesiumProxy: CesiumProxy | null,
  site: SiteListItem | null,
) => {
  if (isNil(cesiumProxy) || isNil(site)) {
    return;
  }

  const properties = getLayerMetadata(layer);

  const destination: Cartographic = properties?.bounds
    ? Rectangle.center(Rectangle.fromDegrees(...properties.bounds))
    : Cartographic.fromDegrees(site.longitude, site.latitude);

  let altitudeAtDestination = cesiumProxy?.getGlobeHeight(destination) ?? 0;

  // altitudeAtDestination will be negative if terrain is not available.
  altitudeAtDestination =
    altitudeAtDestination < 0 ? 5000 : altitudeAtDestination + 5000;

  destination.height = altitudeAtDestination;

  if (layer.type === LayerType.PointCloud) {
    // Try to find the tileset for this point cloud layer and zoom to it
    const tileset = cesiumProxy.layerManager.selectPointCloudLayer(layer.id);
    if (tileset) {
      cesiumProxy.layerManager.zoomToPointCloudLayer(tileset);
      return;
    }
  }

  cesiumProxy?.flyCameraTo({
    destination: Cartographic.toCartesian(destination),
    duration: 1,
  });
};

/**
 * From BE we get bounds as [Long, Lat, Long, Lat]
 *
 * Bounding box of all layers = min Longitude , min Latitude , max Longitude , max Latitude
 *
 * @param layers List of layers for which we want bounds for.
 * @param fallbackSite Site to use as fallback when no layer bounds are available
 * @returns bounds
 */
export const getLayerBounds = (
  layers: WorkspaceLayerListObj,
  fallbackSite: SiteListItem | null,
): BoundingBox | undefined => {
  const layerBounds = Object.values(layers).map((layer) =>
    getLayerMetadata(layer)?.bounds?.map(Number),
  );

  // Remove null/undefined bounds.
  const validLayerBounds = compact(layerBounds);

  if (isEmpty(validLayerBounds)) {
    if (fallbackSite?.latitude && fallbackSite?.longitude) {
      // If no layer bounds are available, use the site's location with a small offset.
      const offset = 0.01;
      const bounds: BoundingBox = [
        fallbackSite.longitude - offset,
        fallbackSite.latitude - offset,
        fallbackSite.longitude + offset,
        fallbackSite.latitude + offset,
      ];
      return bounds;
    }
    return;
  }

  const bounds = validLayerBounds.reduce((previousValue, current) => {
    if (!current || !previousValue) {
      return current || previousValue;
    }

    previousValue[0] = Math.min(previousValue[0], current[0]);
    previousValue[1] = Math.min(previousValue[1], current[1]);
    previousValue[2] = Math.max(previousValue[2], current[2]);
    previousValue[3] = Math.max(previousValue[3], current[3]);

    return previousValue;
  });

  return bounds;
};
